import { useState } from 'react';
import { isEqual } from 'lodash';

import { DropdownOptionType } from '../../types/dropdown';
import { Downloads, ProductsModel } from '../../interface/products';
import { MetaPages } from '../../enums/pages';
import { ENTER_KEY } from '../../constants/searchTabs';
import CustomButton from '../../components/atoms/custom-button/custom-button';
import { useManualsAndGuidesFilter } from '../../hooks/useManualsAndGuidesFilter';
import { getAllProducts } from '../../services/products';
import { ContentType } from '../../types/content';
import SearchButton from '../../components/atoms/search-button/search-button';
import { DROPDOWN_DEFAULT_VALUE } from '../../constants/dropdown';
import DownloadItem from '../../components/atoms/download-item/download-item';
import Tabs from '../../components/molecules/tabs/tabs';
import { inspirationsAndPlanningRoutes } from '../../config/routes/pages';
import HeaderContainer from '../../components/molecules/header-container/header-container';
import SectionContainer from '../../components/atoms/section-container/section-container';
import Dropdown from '../../components/atoms/dropdown/dropdown';
import {
  getManualsAndGuidesBrandOptions,
  getManualsAndGuidesCategoryOptions
} from '../../services/support-and-service';
import { getInspirationsAndPlanningBannerContent } from '../../services/inspirations-and-planning';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import ManualsSearch from '../../components/molecules/manuals-search/manuals-search';

type ManualsAndGuidesProps = {
  brandOptions: DropdownOptionType[];
  categoryOptions: DropdownOptionType[];
  products: ProductsModel[];
  inspirationsAndPlanningBannerContent: ContentType;
};

export function Index(props: ManualsAndGuidesProps) {
  const {
    brandOptions,
    categoryOptions,
    inspirationsAndPlanningBannerContent,
    products
  } = props;
  const [productFilter, setProductFilter] = useState<string>(
    DROPDOWN_DEFAULT_VALUE
  );
  const [selectedProduct, setSelectedProduct] = useState<any | ProductsModel>(
    null
  );
  const [showManuals, setShowManuals] = useState(false);

  const {
    brandFilter,
    categoryFilter,
    filteredDropdownOptions,
    productOptionsList,
    setBrandFilter,
    setCategoryFilter
  } = useManualsAndGuidesFilter(categoryOptions, products);

  function handleSelectBrand(brand: string) {
    setBrandFilter(brand);
  }
  function handleSelectCategory(category: string) {
    setCategoryFilter(category);
  }
  function handleSelectProduct(productName: string) {
    setProductFilter(productName);
  }

  const findSelectedProduct = products.find((product) =>
    [
      product.attributes.ModelName.toLowerCase(),
      product.attributes.ModelNumber.toLowerCase()
    ].includes(productFilter.toLowerCase())
  );

  function handleShowManualsAndGuidesOnEnter(e: any) {
    setSelectedProduct(findSelectedProduct);
    if (isEqual(e, ENTER_KEY)) setShowManuals(true);
  }

  function handleShowManualsAndGuides() {
    setSelectedProduct(findSelectedProduct);
    setShowManuals(true);
  }

  function handleClearInputs() {
    setBrandFilter(DROPDOWN_DEFAULT_VALUE);
    setCategoryFilter(DROPDOWN_DEFAULT_VALUE);
    setProductFilter(DROPDOWN_DEFAULT_VALUE);
    setSelectedProduct(null);
    setShowManuals(false);
  }

  const searchButton = (
    <SearchButton
      className="flex items-center h-full w-auto px-4 bg-white text-white"
      onClick={handleShowManualsAndGuides}
    />
  );

  const downloads = selectedProduct?.attributes.Downloads?.map(
    (download: Downloads) => {
      return <DownloadItem key={download.id} manualAndGuide={download} />;
    }
  );

  const errorMessage = showManuals && !selectedProduct && (
    <p>No guides were found with the given model number or name</p>
  );

  return (
    <div>
      <TabTitle page={MetaPages.TRADE_RESOURCES} />
      <HeaderContainer {...inspirationsAndPlanningBannerContent} />
      <Tabs tabItems={inspirationsAndPlanningRoutes} />
      <SectionContainer>
        <div className="space-y-4 text-center">
          <p className="text-header4 uppercase">Trade Resources</p>
          <p className="text-subtitle">
            Download product manuals and guides here.
          </p>
        </div>
      </SectionContainer>
      <div className="bg-sectionContainerGray pt-8">
        <SectionContainer>
          <div className="flex flex-col items-center space-y-4 px-4">
            <p className="uppercase text-header5 text-center">
              Manuals and Guides Search
            </p>
            <p className="text-body text-center">
              If you&apos;re looking to start designing your dream kitchen,
              you&apos;ve come to the right place. Here&apos;s the inspiration
              you&apos;ll need to help you turn your kitchen goals to reality.
            </p>
          </div>
          <div className="px-6 lg:px-60">
            <ManualsSearch
              button={searchButton}
              className="flex border-dotted border-[1px] border-primaryGray items-center h-10 mt-10"
              formStyle="font-MuseoSans100 text-subtitle"
              placeholder="Search by model number or name"
              setKeyword={handleSelectProduct}
              handleSearch={handleShowManualsAndGuidesOnEnter}
            />
            <p className="text-center text-header5 font-Raleway py-12">OR</p>
            <div className="flex flex-col space-y-4 w-full">
              <Dropdown
                defaultValue={brandFilter}
                onSelect={(brand: string) => handleSelectBrand(brand)}
                dropdownStyle="h-10 text-inputGray text-subtitle border-dotted border-[1px] border-primaryGray bg-white font-MuseoSans100 px-4"
                options={brandOptions}
              />
              <Dropdown
                defaultValue={categoryFilter}
                onSelect={(category: string) => handleSelectCategory(category)}
                dropdownStyle="h-10 text-inputGray text-subtitle border-dotted border-[1px] border-primaryGray bg-white font-MuseoSans100 px-4"
                options={filteredDropdownOptions}
              />
              <Dropdown
                defaultValue={productFilter}
                onSelect={(product: string) => handleSelectProduct(product)}
                dropdownStyle="h-10 text-inputGray text-subtitle border-dotted border-[1px] border-primaryGray bg-white font-MuseoSans100 px-4"
                options={productOptionsList}
              />
            </div>
            <div className="flex justify-between items-center p-4">
              <button onClick={handleClearInputs}>
                <p className="underline text-header6 font-Raleway cursor-pointer hover:text-primaryBlue font-light">
                  Clear Search
                </p>
              </button>
              <CustomButton
                buttonText="Submit"
                buttonStyle="mt-3 p-2 inline-block bg-black text-primaryGray hover:text-white"
                textStyle="px-6 py-1 border-y border-white font-Raleway text-caption"
                handleClickButton={handleShowManualsAndGuides}
              />
            </div>
            <div className="mt-4">
              {downloads} {errorMessage}
            </div>
          </div>
        </SectionContainer>
      </div>
    </div>
  );
}

export async function getStaticProps() {
  const inspirationsAndPlanningBannerContent =
    await getInspirationsAndPlanningBannerContent();
  const categoryOptions = await getManualsAndGuidesCategoryOptions();
  const products = await getAllProducts();
  const brandOptions = await getManualsAndGuidesBrandOptions();

  return {
    props: {
      brandOptions,
      categoryOptions,
      inspirationsAndPlanningBannerContent,
      products
    }
  };
}

export default Index;
